import { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { theme } from '../../styles/theme';

type Counts = {
  products: number;
  variants: number;
  customers: number;
  open_deals: number;
  open_quotes: number;
};

export function AdminDashboard() {
  const [counts, setCounts] = useState<Counts | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    Promise.all([
      supabase.from('products').select('id', { count: 'exact', head: true }),
      supabase.from('product_variants').select('id', { count: 'exact', head: true }),
      supabase.from('customers').select('*', { count: 'exact', head: true }),
      supabase.from('deals').select('*', { count: 'exact', head: true }).eq('status', 'open'),
      supabase.from('quotes').select('*', { count: 'exact', head: true }).in('status', ['sent', 'viewed']),
    ]).then(([p, v, c, d, q]) => {
      if (!alive) return;
      const error = p.error ?? v.error ?? c.error ?? d.error ?? q.error;
      if (error) { setErr(error.message); return; }
      setCounts({
        products: p.count ?? 0,
        variants: v.count ?? 0,
        customers: c.count ?? 0,
        open_deals: d.count ?? 0,
        open_quotes: q.count ?? 0,
      });
    });
    return () => { alive = false; };
  }, []);

  return (
    <div style={{ padding: 32, maxWidth: 1100 }}>
      <div style={{
        fontSize: 11,
        color: theme.accentDark,
        textTransform: 'uppercase',
        letterSpacing: 2,
        fontWeight: 700,
      }}>
        Panel administrativo
      </div>
      <h1 style={{
        fontFamily: theme.font.head,
        fontSize: 32,
        fontWeight: 400,
        color: theme.brand,
        margin: '4px 0 4px',
      }}>
        Dashboard
      </h1>
      <p style={{ color: theme.textMuted, margin: '0 0 32px', fontSize: 14 }}>
        Vista rápida del estado de NULED OS.
      </p>

      {err && (
        <div style={{
          color: theme.err,
          background: '#FEE',
          border: `1px solid ${theme.err}`,
          padding: 12,
          marginBottom: 16,
          fontSize: 14,
        }}>
          Error: {err}
        </div>
      )}

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
        gap: 12,
      }}>
        <Stat label="Productos" value={counts?.products} />
        <Stat label="Variantes" value={counts?.variants} />
        <Stat label="Clientes" value={counts?.customers} />
        <Stat label="Deals abiertos" value={counts?.open_deals} />
        <Stat label="Cotizaciones vivas" value={counts?.open_quotes} />
      </div>

      <div style={{
        marginTop: 32,
        padding: 24,
        background: theme.bgFooter,
        borderLeft: `4px solid ${theme.accent}`,
      }}>
        <div style={{
          fontSize: 11,
          color: theme.accentDark,
          textTransform: 'uppercase',
          letterSpacing: 1.5,
          fontWeight: 700,
          marginBottom: 12,
        }}>
          Roadmap NULED OS
        </div>
        <ul style={{
          margin: 0,
          paddingLeft: 20,
          color: theme.text,
          fontSize: 14,
          lineHeight: 1.9,
        }}>
          <li><strong>Sem 1-2</strong> · Schema + catálogo + frontend público (✓ live)</li>
          <li>Sem 3-4 · CRM + cotizador NULED + Lutron + PDF</li>
          <li>Sem 5-6 · Agente WhatsApp con Sonnet 4.6</li>
          <li>Sem 7-8 · Inventario predictivo + órdenes de compra</li>
          <li>Sem 9-10 · Marketing — Higgsfield, Meta/Google ads</li>
          <li>Sem 11-12 · P&amp;L automático + dashboard ejecutivo</li>
        </ul>
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number | undefined }) {
  return (
    <div style={{
      background: theme.bg,
      border: `1px solid ${theme.border}`,
      padding: 20,
    }}>
      <div style={{
        fontSize: 10,
        color: theme.textMuted,
        textTransform: 'uppercase',
        letterSpacing: 1,
        fontWeight: 700,
      }}>
        {label}
      </div>
      <div style={{
        fontFamily: theme.font.head,
        fontSize: 32,
        fontWeight: 600,
        marginTop: 8,
        color: theme.brand,
      }}>
        {value == null ? '—' : value.toLocaleString('es-MX')}
      </div>
    </div>
  );
}
